import { COMPASS_DIRS, type CompassDir, type WindRange } from "../../lib/types";
import styles from "./CompassRing.module.css";

interface CompassRingProps {
  /** Meteorological direction (degrees, where the wind is coming FROM). */
  windDirectionDeg: number | null;
  /** Wind speed in m/s. Currently unused in the badge but kept for parity with
   *  the map component's prop signature and for future use. */
  windSpeedMs: number | null;
  /** Spot's ideal wind range. The arc highlights these direction sectors. */
  ideal: WindRange;
  /** Spot's secondary wind range. Currently not visualised — design has only
   *  two states (ideal match / no match) — kept for future use. */
  secondary?: WindRange;
}

// Compass dimensions. Bigger than the original Figma 200×200 spec — the disc
// is now a donut so the centre badge sits over the live basemap. Layered:
//   centre pill  ◄ hole (35)  ◄ degree labels (45)  ◄ ticks (56..64)
//   ◄ N/E/S/W letters (75)  ◄ arrow tip (82)  ◄ arc (95)
//   ◄ donut edge (97)  ◄ arrow base (106)
const SIZE = 215;
const CENTER = SIZE / 2;
const DISC_OUTER_R = 97; // outer edge of the translucent navy donut
const DISC_INNER_R = 35; // inner edge — small hole so the centre tag pill
                          // sits on the live basemap, not on the donut fill
const ARC_R = 95;        // ideal-direction arc sits just inside donut outer
const TICK_INNER_R = 56; // inner end of tick marks
// 0°/90°/180°/270° on the INNER ring, rotated tangentially so each label
// reads along the rim (nautical compass-rose convention). Sits just
// outside the centre-tag hole.
const DEGREE_LABEL_R = 45;
// N/E/S/W letters on the OUTER ring, upright. Sits in the free band
// between the outermost cardinal tick (r=64) and the arrow tip (r=82).
const LETTER_R = 75;
const ARROW_TIP_R = 82;  // arrow tip (closer to center)
const ARROW_BASE_R = 106; // arrow base corners (just inside outer edge)
const ARROW_HALF_W = 9;  // arrow half-width at the base — bigger arrowhead

// ±22.5° per ideal direction — same tolerance the scorer uses
// (`scorer.ts`: `minIdeal <= 22.5` is the threshold for "good" match /
// `prime` level). Mirroring the scorer here is critical so the compass
// "Dir OK" badge agrees with the chart's green-arrow / prime-bar colour
// in every case. Visually the per-direction arc covers 45° (twice this
// half-width) — wider than each direction's 22.5° ownership slice, but
// that's intentional: the arc represents the WIND ANGLES that match
// the ideal, not the cardinal-direction labels themselves.
const ARC_HALF_WIDTH = 22.5;

function compassDirToDeg(dir: CompassDir): number {
  return COMPASS_DIRS.indexOf(dir) * 22.5;
}

/** Shortest angular distance between two bearings (0–180). */
function angleDiff(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/** Whether a bearing falls within ARC_HALF_WIDTH of any of the directions. */
function isInSector(deg: number, dirs: readonly CompassDir[]): boolean {
  return dirs.some((dir) => angleDiff(deg, compassDirToDeg(dir)) <= ARC_HALF_WIDTH);
}

/** Convert polar (origin top, clockwise) → cartesian on the SVG canvas. */
function polar(r: number, deg: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return {
    x: CENTER + r * Math.sin(rad),
    y: CENTER - r * Math.cos(rad),
  };
}

/** Stroke-only arc path between two bearings. */
function arcPath(r: number, startDeg: number, endDeg: number): string {
  const start = polar(r, startDeg);
  const end = polar(r, endDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${start.x},${start.y} A ${r},${r} 0 ${largeArc} 1 ${end.x},${end.y}`;
}

/**
 * Merge consecutive (in compass order) directions into single arc segments
 * so e.g. ["N", "NNE", "NE"] becomes one continuous arc rather than three
 * adjoining strokes. Handles wrap-around (NW → N).
 */
function mergeArcs(
  dirs: readonly CompassDir[],
): Array<{ startDeg: number; endDeg: number }> {
  if (dirs.length === 0) return [];
  // Sort by index in COMPASS_DIRS so we can detect runs of consecutive sectors.
  const indices = dirs
    .map((d) => COMPASS_DIRS.indexOf(d))
    .sort((a, b) => a - b);
  const arcs: Array<{ startIdx: number; endIdx: number }> = [];
  let runStart = indices[0]!;
  let runEnd = indices[0]!;
  for (let i = 1; i < indices.length; i++) {
    const idx = indices[i]!;
    if (idx === runEnd + 1) {
      runEnd = idx;
    } else {
      arcs.push({ startIdx: runStart, endIdx: runEnd });
      runStart = idx;
      runEnd = idx;
    }
  }
  arcs.push({ startIdx: runStart, endIdx: runEnd });

  // Wrap-around merge: if first run starts at 0 AND last run ends at 15,
  // they're actually one continuous arc that crosses the seam.
  if (arcs.length >= 2) {
    const first = arcs[0]!;
    const last = arcs[arcs.length - 1]!;
    if (first.startIdx === 0 && last.endIdx === COMPASS_DIRS.length - 1) {
      arcs.pop();
      arcs[0] = { startIdx: last.startIdx - COMPASS_DIRS.length, endIdx: first.endIdx };
    }
  }

  return arcs.map(({ startIdx, endIdx }) => ({
    startDeg: startIdx * 22.5 - ARC_HALF_WIDTH,
    endDeg: endIdx * 22.5 + ARC_HALF_WIDTH,
  }));
}

export function CompassRing({
  windDirectionDeg,
  ideal,
  secondary,
}: CompassRingProps) {
  // Three tiers, mirroring the chart's arrow colour rules (see
  // `dirLevelColor()` in lib/utils.ts and docs/decisions.md):
  //   ideal  → prime green
  //   secondary → good blue
  //   neither   → compass-foreground (white)
  const idealMatch =
    windDirectionDeg != null && isInSector(windDirectionDeg, ideal.dirs);
  const secondaryMatch =
    !idealMatch &&
    windDirectionDeg != null &&
    secondary != null &&
    isInSector(windDirectionDeg, secondary.dirs);

  // Custom locations (dropped pin) have no ideal/secondary ranges — the tag
  // would always read "Dir OFF", which is meaningless without an "optimal"
  // to compare against. Hide it entirely in that case.
  const hasIdealRange = ideal.dirs.length > 0;
  const tagText = idealMatch ? "Dir OK" : secondaryMatch ? "Dir 2nd" : "Dir OFF";
  // Tag pill text picks up the direction-match accent: prime green for
  // ideal, good blue for secondary, white for off. The arrowhead itself
  // stays white in every state — the colour cue lives on the arc on the
  // outer ring (and on the tag text), not on the arrow.
  const tagColor = idealMatch
    ? "var(--wind-direction-prime-default)"
    : secondaryMatch
      ? "var(--wind-direction-good-default)"
      : "var(--compass-foreground)";
  const arrowFill = "var(--compass-foreground)";

  // Arc renders both ideal AND secondary direction zones so the user sees
  // every wind angle that yields a coloured arrow, not just the ideal ones.
  const idealArcs = mergeArcs(ideal.dirs);
  const secondaryArcs = secondary?.dirs ? mergeArcs(secondary.dirs) : [];

  // Tag pill width: reserve enough for "Dir OFF" (the wider label).
  const TAG_W = 56;
  const TAG_H = 20; // matches `--tag-height`

  return (
    <div className={styles.wrap} style={{ width: SIZE, height: SIZE }}>
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className={styles.svg}
      >
        {/* Translucent dark donut behind the dial. Drawn as a stroked
            circle: the stroke runs at the midline radius with a width =
            donut thickness, so the visible band sits between
            DISC_INNER_R and DISC_OUTER_R. The hole leaves the centre
            badge sitting on the live basemap. */}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={(DISC_OUTER_R + DISC_INNER_R) / 2}
          fill="none"
          stroke="var(--compass-background)"
          strokeWidth={DISC_OUTER_R - DISC_INNER_R}
        />

        {/* Outer perimeter — `compass/border` per Figma. Defines the
            edge of the disc against the basemap; lives at radius
            DISC_OUTER_R so it sits exactly on the outside of the
            translucent fill above. */}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={DISC_OUTER_R}
          fill="none"
          stroke="var(--compass-border)"
          style={{ strokeWidth: "var(--border-width)" }}
        />

        {/* Inner tick marks at every 22.5°. Lengths shrink toward inter-cardinal
            so cardinals read as the "long" hashes. Outer end stays clear of
            the degree-label ring at radius DEGREE_R. */}
        {COMPASS_DIRS.map((dir, i) => {
          const deg = i * 22.5;
          const isCardinal = ["N", "E", "S", "W"].includes(dir);
          const isOrdinal = ["NE", "SE", "SW", "NW"].includes(dir);
          const len = isCardinal ? 8 : isOrdinal ? 6 : 4;
          const inner = polar(TICK_INNER_R, deg);
          const outer = polar(TICK_INNER_R + len, deg);
          return (
            <line
              key={`tick-${dir}`}
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
              stroke="var(--compass-lines)"
              strokeWidth={isCardinal ? 1.5 : 1}
              strokeLinecap="round"
            />
          );
        })}

        {/* Secondary-direction arc(s). Drawn first so ideal renders on top
            wherever the two ranges overlap (rare but possible). Butt
            linecaps so the arc terminates cleanly at its angular bounds. */}
        {secondaryArcs.map((arc, i) => (
          <path
            key={`sec-arc-${i}`}
            d={arcPath(ARC_R, arc.startDeg, arc.endDeg)}
            fill="none"
            stroke="var(--wind-direction-good-default)"
            strokeWidth={4}
            opacity={0.7}
          />
        ))}

        {/* Ideal-direction arc(s) — each run of consecutive sectors becomes
            one continuous arc with butt linecaps (so the seams disappear). */}
        {idealArcs.map((arc, i) => (
          <path
            key={`ideal-arc-${i}`}
            d={arcPath(ARC_R, arc.startDeg, arc.endDeg)}
            fill="none"
            stroke="var(--wind-direction-prime-default)"
            strokeWidth={4}
          />
        ))}

        {/* Cardinal LETTER labels (N/E/S/W) on the outer ring, upright —
            sit in the free band between the ticks and the arrow tip so
            they never paint over the coloured arc. */}
        {(["N", "E", "S", "W"] as CompassDir[]).map((dir) => {
          const deg = compassDirToDeg(dir);
          const pos = polar(LETTER_R, deg);
          return (
            <text
              key={`letter-${dir}`}
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="central"
              className={styles.letter}
            >
              {dir}
            </text>
          );
        })}

        {/* Cardinal DEGREE labels (0°/90°/180°/270°) on the inner ring,
            rotated tangentially so they read along the rim (nautical
            compass-rose convention). */}
        {[0, 90, 180, 270].map((deg) => {
          const pos = polar(DEGREE_LABEL_R, deg);
          return (
            <text
              key={`deg-${deg}`}
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="central"
              transform={`rotate(${deg} ${pos.x} ${pos.y})`}
              className={styles.degree}
            >
              {deg}°
            </text>
          );
        })}

        {/* Wind FROM direction: arrow on the outer ring pointing inward */}
        {windDirectionDeg != null && (
          <g
            transform={`rotate(${windDirectionDeg} ${CENTER} ${CENTER})`}
            style={{ transition: "transform 280ms cubic-bezier(.5,.1,.3,1)" }}
          >
            <polygon
              points={[
                `${CENTER},${CENTER - ARROW_TIP_R}`,
                `${CENTER - ARROW_HALF_W},${CENTER - ARROW_BASE_R}`,
                `${CENTER + ARROW_HALF_W},${CENTER - ARROW_BASE_R}`,
              ].join(" ")}
              fill={arrowFill}
              stroke="var(--compass-arrow-border)"
              strokeWidth={1}
              strokeLinejoin="round"
            />
          </g>
        )}

        {/* Centre tag pill: "Dir OK" / "Dir OFF". `rx`/`ry` flow through
            CSS so the corner radius stays tied to `--radius-xs`. Hidden for
            custom locations where there's no ideal direction to compare. */}
        {hasIdealRange && (
          <>
            <rect
              x={CENTER - TAG_W / 2}
              y={CENTER - TAG_H / 2}
              width={TAG_W}
              height={TAG_H}
              fill="var(--overlay-hover)"
              style={{ rx: "var(--radius-xs)", ry: "var(--radius-xs)" }}
            />
            <text
              x={CENTER}
              y={CENTER}
              textAnchor="middle"
              dominantBaseline="central"
              className={styles.tagText}
              fill={tagColor}
            >
              {tagText}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}
